/**
 * Spec §4 severity → StatusBannerVariant mapping (consumer-side projection).
 *
 * The shared @metro/station-status module stays domain-agnostic and exposes
 * only the raw StationStatus. The mapping from server data to visual state
 * lives here, in the consumer (StationCard), per the spec:
 *
 *   closed==true                  → 'closed'    (danger)
 *   severity=='closure' && !closed → 'restricted' (warning)
 *   severity=='advisory'           → 'advisory'  (soft banner)
 *   absent from snapshot           → undefined   (Open — no banner)
 *
 * Mirrors iOS StationCardSystem.resolvedStatus and Android StationCard mapping.
 */
import type { StationStatusSnapshot } from '@metro/station-status'
import type { StatusBannerVariant } from './StatusBanner'

/**
 * Resolve the StatusBannerVariant for a card given its naptanId and the
 * current status snapshot. Returns `undefined` when the card has no naptanId,
 * or when the naptanId is absent from the snapshot (Open state).
 *
 * This is the consumer-side projection described in spec §4. It runs on every
 * render so the card reacts to snapshot updates in place without remounting.
 */
export function resolveStatusVariant(
  naptanId: string | undefined,
  statuses: StationStatusSnapshot,
): StatusBannerVariant | undefined {
  if (naptanId === undefined) return undefined
  const status = statuses[naptanId]
  if (status === undefined) return undefined

  if (status.closed) return 'closed'
  if (status.severity === 'closure') return 'restricted'
  return 'advisory'
}
